import { format } from "util";

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";

import { ENVIRONMENT, Environment, FileDownloadService, Slot, Store, Viewstate, WebSocketService } from "@vattenfall/core";
import { BooleanInput, coerceBooleanProperty, getURL } from "@vattenfall/util";

import { Subscription, interval } from "rxjs";
import { delay, tap } from "rxjs/operators";

import {
  ChatAppElementModel,
  ChatAppMessageInitialize,
  ChatAppMessagePollAgent,
  ChatAppMessageType,
  ChatAppMessages,
} from "./chat-app.model";

import { VIEWSTATES } from "./chat-app.viewstates";

export const STORAGE_KEY = "ChatAppElement";

export interface ChatAppStateModel {
  sessionId: string;
  messages: Array<ChatAppMessages>;
  metadata: {
    currentPage: string;
    parameters: string;
    initialized: boolean;
    unreadMessageCount: number;
    interactionWithAgent: boolean;
    agentInfo: string;
    callbackMessage: string;
  };
}

/**
 * Chat application allowing for client-server conversations.
 *
 * Will occupy a fixed position on the bottom right corner of the page, and expand once enabled by the user.
 *
 * Supports different sets of messages depending on what is received from the server.
 */
@Component({
  selector: "chat-app",
  templateUrl: "./chat-app.element.html",
  styleUrls: ["./chat-app.element.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WebSocketService],
})
export class ChatAppElement implements ChatAppElementModel, OnInit, OnDestroy, AfterViewInit {
  @Input() @Viewstate(VIEWSTATES) viewstate?: string;

  /** Hidden message to be sent when the chat is initialized to show intent. */
  @Input() sessionMessage = "hej";

  /** Time until a tooltip should appear (over the chat icon) expressed in milliseconds. */
  @Input() tooltipDelay = 3000;

  /** Whether the chat should be visible to the user on the given page. */
  @Input()
  get visible(): boolean {
    return this._visible;
  }
  set visible(value: BooleanInput) {
    this._visible = coerceBooleanProperty(value);
  }
  _visible = false;

  @Slot() readonly chatAppLabel?: string;
  @Slot() readonly chatAppInputLabel?: string;
  @Slot() readonly chatAppTooltipLabel?: string;
  @Slot() readonly chatAppException?: string;
  @Slot() readonly chatAppDownloadHistoryButtonLabel?: string;

  @ViewChild("ngForm") ngForm!: NgForm;
  @ViewChild("messageWrapperElement") messageWrapperElement!: ElementRef<HTMLDivElement>;

  /** Whether the chat has been initialized or not. */
  initialized = false;

  /** Whether the chat window is open. */
  open = false;

  /** Whether the tooltip is open. */
  openTooltip = false;

  /** Whether the chat has failed. */
  error = false;

  /** Whether server is processing. */
  loading = false;

  /** Unique identifier for the session. */
  sessionId = "";

  /** List of messages for the session. */
  messages: Array<ChatAppMessages> = [];

  /** Holds the count for unread messages. */
  unreadMessageCount = 0;

  /** Holds the current page values used for extending a session. Read more here: https://cloud.google.com/dialogflow/cx/docs/concept/session */
  private currentPage = "";

  /** Holds the current page parameters used for extending a session. Read more here: https://cloud.google.com/dialogflow/cx/docs/concept/session */
  private parameters = "";

  /** Placeholder to know if user is interacting with agent or not */
  private get interactionWithAgent() {
    return this.store.getStorage(STORAGE_KEY)?.metadata?.interactionWithAgent;
  }

  /**
   * Delay response time from bot to simulate a real conversation.
   * When talking to live agent, we return immediately. We already have a delay in the polling.
   * @returns {number} Zero milliseconds if talking to live agent, otherwise 1500 milliseconds.
   */
  private get messageDelay() {
    return this.interactionWithAgent ? 0 : 1500;
  }

  private webSocketSubscription = Subscription.EMPTY;

  private pollAgentIntervalSubscription = Subscription.EMPTY;

  constructor(
    @Optional()
    @Inject(ENVIRONMENT)
    private environment: Environment,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store<ChatAppStateModel>,
    private webSocketService: WebSocketService<ChatAppMessages>,
    private fileDownloadService: FileDownloadService
  ) {
    this.store.storageIdentifier = ".";
  }

  ngOnInit() {
    if (!this.viewstate) {
      // Always show the chat if the user has an open chat session (from some other page)
      if (this.store.getStorage(STORAGE_KEY).sessionId) {
        this.visible = true;
        this.initialize();
      }

      if (this.visible) {
        // Display the tooltip message
        setTimeout(this.setTooltip(true), this.tooltipDelay);
        // Make sure to close the message after a duration of time
        setTimeout(this.setTooltip(false), this.tooltipDelay * 6);
      }
    }
  }

  ngAfterViewInit() {
    if (this.visible)
      // Keeps track of when messages are added into the view and scrolls to the last message
      new MutationObserver(this.scrollIntoView).observe(this.messageWrapperElement.nativeElement, {
        subtree: true,
        childList: true,
      });

    if (this.interactionWithAgent) {
      // We are having an ongoing conversation with an agent,
      // We force initialize the chat.
      // Otherwise the chat is intialized when the user clicks on the chat icon and opens the chat window.
      this.initialize();
      this.startAgentPolling();
    }
  }

  ngOnDestroy() {
    // Fix for 'TypeError: Cannot read properties of undefined' in Storybook
    if (!this.viewstate) {
      if (this.webSocketSubscription) this.webSocketSubscription.unsubscribe();
      this.webSocketService.close();
    }
    // Stop any potential polling, by sending interactionWithAgent as false when we kill the component.
    this.stopAgentPolling();
  }

  onToggle() {
    if (!this.viewstate && !this.initialized) this.initialize();
    if (!this.open) {
      this.unreadMessageCount = 0;
      this.openTooltip = false;
      this.store.patchStorage(STORAGE_KEY, {
        metadata: {
          ...this.store.getStorage(STORAGE_KEY)?.metadata,
          unreadMessageCount: 0,
        },
      });
    }
    this.open = !this.open;
    this.scrollIntoView();
  }

  onSubmit(message: string) {
    this.ngForm.resetForm();
    this.setMessage({
      messageType: ChatAppMessageType.User,
      currentPage: this.currentPage,
      parameters: this.parameters,
      ...(this.interactionWithAgent && {
        agentInfo: this.store.getStorage(STORAGE_KEY).metadata.agentInfo,
      }),
      message,
    });
  }

  onEnter(event: Event) {
    event.preventDefault();
    if (this.ngForm.invalid) return;
    this.onSubmit(this.ngForm.value.message);
  }

  private initialize() {
    // Save session key in storage so the chat session can persist over page reloads
    if (!this.store.getStorage(STORAGE_KEY).sessionId)
      this.store.setStorage(STORAGE_KEY, {
        sessionId: this.store.id,
        messages: [],
        metadata: {
          currentPage: "",
          parameters: "",
          initialized: false,
          unreadMessageCount: 0,
          interactionWithAgent: false,
        },
      });
    const storage = this.store.getStorage(STORAGE_KEY);
    // Load conversation from storage
    this.messages = storage.messages;
    this.sessionId = storage.sessionId && this.store.id;
    this.currentPage = storage.metadata.currentPage;
    this.parameters = storage.metadata.parameters;
    this.initialized = storage.metadata.initialized;
    this.unreadMessageCount = storage.metadata.unreadMessageCount;
    this.changeDetectorRef.markForCheck();
    // Establish connection once the session ID has been resolved
    this.webSocketService.connect(getURL(this.environment.endpoints.chatApi, { sessionId: this.sessionId }));
    if (this.webSocketService.messages)
      this.webSocketSubscription = this.webSocketService.messages
        ?.pipe(
          tap(this.setLoading),
          delay(this.messageDelay),
          tap((message: ChatAppMessages) => {
            if (message?.messageType === ChatAppMessageType.SwitchAgent) {
              this.store.patchStorage(STORAGE_KEY, {
                metadata: {
                  ...this.store.getStorage(STORAGE_KEY)?.metadata,
                  agentInfo: message.agentInfo,
                  interactionWithAgent: true,
                },
              });
              this.sendChatHistory();
              this.startAgentPolling();
            }
            if (message?.messageType === ChatAppMessageType.SwitchBot) {
              this.store.patchStorage(STORAGE_KEY, {
                metadata: {
                  ...this.store.getStorage(STORAGE_KEY)?.metadata,
                  agentInfo: "",
                  interactionWithAgent: false,
                },
              });
              this.stopAgentPolling();
            }
            if (message?.messageType === ChatAppMessageType.Agent) {
              this.store.patchStorage(STORAGE_KEY, {
                metadata: { ...this.store.getStorage(STORAGE_KEY)?.metadata, agentInfo: message.agentInfo },
              });
            }
            return message;
          })
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .subscribe(this.setMessage, this.setError);

    // Read storage for callback message. If it exists, it means that we need to recover a conversation.
    const { callbackMessage } = this.store.getStorage(STORAGE_KEY).metadata;

    // Prioritize callback message over session message.
    // If there is no callback message, we use the session message to initiate the conversation.
    const initMessage: ChatAppMessageInitialize = {
      messageType: ChatAppMessageType.Initialize,
      message: callbackMessage || this.sessionMessage,
    };
    // Clear callback message from storage
    if (callbackMessage) {
      this.store.patchStorage(STORAGE_KEY, {
        metadata: {
          ...this.store.getStorage(STORAGE_KEY)?.metadata,
          callbackMessage: undefined,
        },
      });
    }

    // Start the conversation if there is a callback message. Meaning we need to recover a conversation.
    // Or if the chat is not initialized, meaning we need to start a new conversation.
    if (callbackMessage || !this.initialized) this.webSocketService.send(initMessage);
  }

  private setMessage = (message: ChatAppMessages) => {
    // Process messages should not be handled
    if (message.messageType === ChatAppMessageType.Process) return;
    else this.loading = false;
    if (!this.initialized) {
      this.initialized = true;
      this.store.patchStorage(STORAGE_KEY, {
        metadata: {
          ...this.store.getStorage(STORAGE_KEY)?.metadata,
          initialized: true,
        },
      });
    }
    if (!this.open) {
      this.unreadMessageCount++;
      this.store.patchStorage(STORAGE_KEY, {
        metadata: {
          ...this.store.getStorage(STORAGE_KEY)?.metadata,
          unreadMessageCount: this.unreadMessageCount,
        },
      });
    }

    if (!this.viewstate) {
      this.currentPage = message.currentPage ?? "";
      this.parameters = message.parameters ?? "";
      this.store.patchStorage(STORAGE_KEY, {
        messages: this.messages,
        metadata: {
          ...this.store.getStorage(STORAGE_KEY)?.metadata,
          currentPage: message.currentPage,
          parameters: message.parameters,
        },
      });
      if (message.messageType === ChatAppMessageType.User) {
        const { interactionWithAgent, agentInfo } = this.store.getStorage(STORAGE_KEY).metadata;
        if (interactionWithAgent) message.agentInfo = agentInfo;
        this.webSocketService.send(message);
      }
    }
    message.timestamp = new Date();
    this.messages.push(message);
    this.store.patchStorage(STORAGE_KEY, { messages: this.messages });
    this.changeDetectorRef.markForCheck();
  };

  private setLoading = (message: ChatAppMessages) => {
    if (message.messageType === ChatAppMessageType.Process) this.loading = true;
    this.changeDetectorRef.markForCheck();
  };

  private setError = () => {
    this.error = true;
    this.changeDetectorRef.markForCheck();
  };

  private setTooltip = (open: boolean) => () => {
    if (this.chatAppTooltipLabel && !this.initialized && !this.error) {
      this.openTooltip = open;
      this.changeDetectorRef.markForCheck();
    }
  };

  private scrollIntoView = () => {
    // Scroll to last message
    this.messageWrapperElement.nativeElement.scroll({
      top: this.messageWrapperElement.nativeElement.scrollHeight,
    });
    // Ensures that message isFirst and isLast is checked correctly
    this.changeDetectorRef.markForCheck();
  };

  private startAgentPolling() {
    const sendAgentMessage = () => {
      const { agentInfo } = this.store.getStorage(STORAGE_KEY).metadata;
      const pollAgentMessage: ChatAppMessagePollAgent = {
        messageType: ChatAppMessageType.PollAgent,
        agentInfo: agentInfo,
      };
      this.webSocketService?.send(pollAgentMessage);
    };
    const pollAgentInterval$ = interval(3700);
    this.pollAgentIntervalSubscription = pollAgentInterval$.subscribe(sendAgentMessage);
  }
  private stopAgentPolling = () => {
    this.pollAgentIntervalSubscription.unsubscribe();
  };

  /**
   * Sends the chat history via websocket.
   * Among other situations, this is used when the user switches from bot to agent.
   * All the messages until this point are converted to ChatMessagType.User and sent via websocket service.
   */
  private sendChatHistory = () => {
    const agentInfo = this.store.getStorage(STORAGE_KEY).metadata.agentInfo;

    /** Start the conversation messages with a line wrapper */
    const startMessage: ChatAppMessages = {
      messageType: ChatAppMessageType.User,
      message: "########### This is what what the user said to me ###########",
      agentInfo: agentInfo,
    };
    this.webSocketService?.send(startMessage);
    /** Convert and send to websocket service all the messages until this point. */
    const formattedMessages = this.formattedChatMessages();
    formattedMessages.forEach((msg?: string) => {
      if (msg)
        this.webSocketService?.send({
          messageType: ChatAppMessageType.User,
          message: msg,
          agentInfo: agentInfo,
        });
    });
    /** The end message to mark the conversation end for UX purposes in the agent side. */
    const endMessage: ChatAppMessages = {
      messageType: ChatAppMessageType.User,
      message: "########### Regards, Elliot!  ###########",
      agentInfo: agentInfo,
    };
    this.webSocketService?.send(endMessage);
  };
  onClearHistory() {
    // Clear messages
    this.messages = [];
    // Clear storage messages
    this.store.patchStorage(STORAGE_KEY, { messages: [] });
    // Re-initialize the chat
    this.webSocketService.send({
      messageType: ChatAppMessageType.Initialize,
      message: this.sessionMessage,
    });
  }
  onLinkClick(message: string) {
    this.store.patchStorage(STORAGE_KEY, {
      metadata: {
        ...this.store.getStorage(STORAGE_KEY)?.metadata,
        callbackMessage: message,
      },
    });
  }
  private getActor = (message: ChatAppMessages): string => {
    // TODO: Use hardcoded strings for now, but should be replaced with EPi slots
    switch (message.messageType) {
      case ChatAppMessageType.User:
        return "Användare";
      case ChatAppMessageType.Bot:
        return "Bot";
      case ChatAppMessageType.Agent:
        return "Agent";
      case ChatAppMessageType.Links:
        return "Användare";
      default:
        return "Bot";
    }
  };
  private formattedChatMessages = () => {
    // Map messages
    const formattedMessages = this.messages.map((msg) => {
      // Add type guard to check if 'message' property exists
      // We are only interested in messages with a 'message' property
      if (!("message" in msg) || !msg.message) return;
      return `${this.getActor(msg)}: ${msg.message} \n`;
    });

    return formattedMessages;
  };

  onDownloadHistory() {
    const formattedMessages = this.formattedChatMessages();

    this.fileDownloadService.download(
      formattedMessages.join("\n"),
      "text/plain",
      `Vattenfall_Försäljning_${format(new Date(), "yyyy-MM-dd hhmm")}.txt`
    );
  }
}
